import React, { useState, useCallback } from 'react';
import type { Socket } from 'socket.io-client';

interface ConstructionKitDevPanelProps {
  socket?: Socket | null;
  playerId?: string;
  apiBaseUrl: string;
  isAdminVerified: boolean;
  isProdMode: boolean;
}

type KitType = 'wood' | 'stone' | 'metal';
type ItemCategory = 'construction' | 'sign';

interface SpawnResult {
  success: boolean;
  message: string;
  constructionId?: string;
  worldX?: number;
  worldY?: number;
}

export const ConstructionKitDevPanel: React.FC<ConstructionKitDevPanelProps> = ({
  socket,
  playerId,
  apiBaseUrl,
  isAdminVerified,
  isProdMode,
}) => {
  const [category, setCategory] = useState<ItemCategory>('construction');
  const [kitType, setKitType] = useState<KitType>('wood');
  const [quantity, setQuantity] = useState<string>('1');
  const [spawnX, setSpawnX] = useState<string>('0');
  const [spawnY, setSpawnY] = useState<string>('0');
  const [usePlayerPos, setUsePlayerPos] = useState<boolean>(true);
  const [isSpawning, setIsSpawning] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<SpawnResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canUseDevTools = isProdMode ? isAdminVerified : true;

  const spawnItems = useCallback(async (): Promise<void> => {
    if (!canUseDevTools) {
      setError(isProdMode ? 'Verify ADMIN_KEY to use dev tools' : 'Dev tools disabled');
      return;
    }

    if (!socket || !playerId) {
      setError('Socket not connected or player ID missing');
      return;
    }

    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) {
      setError('Quantity must be a positive number');
      return;
    }

    setIsSpawning(true);
    setError(null);
    setLastResult(null);

    try {
      const itemId = category === 'construction' 
        ? `construction_kit_${kitType}` 
        : `sign_${kitType}`;

      socket.emit('dev:spawn_construction_kits', {
        kitType: category === 'construction' ? kitType : undefined,
        itemId: category === 'sign' ? itemId : undefined,
        quantity: qty,
        usePlayerPosition: usePlayerPos,
        worldX: usePlayerPos ? undefined : parseInt(spawnX, 10),
        worldY: usePlayerPos ? undefined : parseInt(spawnY, 10),
      }, (response: SpawnResult) => {
        setLastResult(response);
        if (!response.success) {
          setError(response.message);
        }
      });
    } catch (err) {
      setError(`Error spawning items: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSpawning(false);
    }
  }, [socket, playerId, category, kitType, quantity, usePlayerPos, spawnX, spawnY, canUseDevTools, isProdMode]);

  const validatePosition = useCallback(async (): Promise<void> => {
    if (!canUseDevTools) {
      setError(isProdMode ? 'Verify ADMIN_KEY to use dev tools' : 'Dev tools disabled');
      return;
    }

    const x = parseInt(spawnX, 10);
    const y = parseInt(spawnY, 10);

    if (isNaN(x) || isNaN(y)) {
      setError('Invalid coordinates');
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/dev/validate-construction-position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ worldX: x, worldY: y }),
      });

      const data = await response.json() as { valid: boolean; reason?: string };
      if (data.valid) {
        setError(null);
        setLastResult({ success: true, message: 'Position is valid for construction' });
      } else {
        setError(data.reason || 'Position is invalid');
      }
    } catch (err) {
      setError(`Validation error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [spawnX, spawnY, apiBaseUrl, canUseDevTools, isProdMode]);

  return (
    <div style={{ padding: '8px', borderBottom: '1px solid #3a3a5a' }}>
      <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px' }}>
        🔨 Item Spawner
      </div>

      {!canUseDevTools && (
        <div style={{ color: '#e74c3c', fontSize: '10px', marginBottom: '6px' }}>
          {isProdMode ? 'Verify ADMIN_KEY to use dev tools' : 'Dev tools disabled'}
        </div>
      )}

      {/* Category Selection */}
      <div style={{ marginBottom: '6px' }}>
        <label style={{ display: 'block', fontSize: '10px', color: '#b0b0b0', marginBottom: '2px' }}>
          Category:
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ItemCategory)}
          disabled={isSpawning || !canUseDevTools}
          style={{
            width: '100%',
            padding: '4px',
            backgroundColor: '#2a2a4e',
            border: '1px solid #4a4a6a',
            color: '#e0e0e0',
            cursor: isSpawning || !canUseDevTools ? 'not-allowed' : 'pointer',
            opacity: isSpawning || !canUseDevTools ? 0.6 : 1,
          }}
        >
          <option value="construction">🔨 Construction Kits</option>
          <option value="sign">📝 Signs</option>
        </select>
      </div>

      {/* Material Type Selection */}
      <div style={{ marginBottom: '6px' }}>
        <label style={{ display: 'block', fontSize: '10px', color: '#b0b0b0', marginBottom: '2px' }}>
          Material:
        </label>
        <select
          value={kitType}
          onChange={(e) => setKitType(e.target.value as KitType)}
          disabled={isSpawning || !canUseDevTools}
          style={{
            width: '100%',
            padding: '4px',
            backgroundColor: '#2a2a4e',
            border: '1px solid #4a4a6a',
            color: '#e0e0e0',
            cursor: isSpawning || !canUseDevTools ? 'not-allowed' : 'pointer',
            opacity: isSpawning || !canUseDevTools ? 0.6 : 1,
          }}
        >
          <option value="wood">🪵 Wood</option>
          <option value="stone">🪨 Stone</option>
          <option value="metal">⚙️ Metal</option>
        </select>
      </div>

      {/* Quantity */}
      <div style={{ marginBottom: '6px' }}>
        <label style={{ display: 'block', fontSize: '10px', color: '#b0b0b0', marginBottom: '2px' }}>
          Quantity:
        </label>
        <input
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          disabled={isSpawning || !canUseDevTools}
          min="1"
          max="100"
          style={{
            width: '100%',
            padding: '4px',
            backgroundColor: '#2a2a4e',
            border: '1px solid #4a4a6a',
            color: '#e0e0e0',
            cursor: isSpawning || !canUseDevTools ? 'not-allowed' : 'text',
            opacity: isSpawning || !canUseDevTools ? 0.6 : 1,
          }}
        />
      </div>

      {/* Position Selection */}
      <div style={{ marginBottom: '6px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={usePlayerPos}
            onChange={(e) => setUsePlayerPos(e.target.checked)}
            disabled={isSpawning || !canUseDevTools}
            style={{ cursor: isSpawning || !canUseDevTools ? 'not-allowed' : 'pointer' }}
          />
          <span>Use Player Position</span>
        </label>
      </div>

      {!usePlayerPos && (
        <div style={{ marginBottom: '6px', padding: '6px', backgroundColor: '#2a2a4e', borderRadius: '4px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '4px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '9px', color: '#b0b0b0', marginBottom: '2px' }}>
                World X:
              </label>
              <input
                type="number"
                value={spawnX}
                onChange={(e) => setSpawnX(e.target.value)}
                disabled={isSpawning || !canUseDevTools}
                style={{
                  width: '100%',
                  padding: '4px',
                  backgroundColor: '#1a1a3e',
                  border: '1px solid #4a4a6a',
                  color: '#e0e0e0',
                  fontSize: '10px',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '9px', color: '#b0b0b0', marginBottom: '2px' }}>
                World Y:
              </label>
              <input
                type="number"
                value={spawnY}
                onChange={(e) => setSpawnY(e.target.value)}
                disabled={isSpawning || !canUseDevTools}
                style={{
                  width: '100%',
                  padding: '4px',
                  backgroundColor: '#1a1a3e',
                  border: '1px solid #4a4a6a',
                  color: '#e0e0e0',
                  fontSize: '10px',
                }}
              />
            </div>
          </div>
          <button
            onClick={validatePosition}
            disabled={isSpawning || !canUseDevTools}
            style={{
              width: '100%',
              padding: '4px',
              backgroundColor: '#9b59b6',
              border: 'none',
              color: 'white',
              fontSize: '10px',
              cursor: isSpawning || !canUseDevTools ? 'not-allowed' : 'pointer',
              opacity: isSpawning || !canUseDevTools ? 0.6 : 1,
            }}
          >
            Validate Position
          </button>
        </div>
      )}

      {/* Spawn Button */}
      <button
        onClick={spawnItems}
        disabled={isSpawning || !canUseDevTools}
        style={{
          width: '100%',
          padding: '6px',
          backgroundColor: isSpawning ? '#555' : '#2ecc71',
          border: 'none',
          color: 'white',
          fontWeight: 'bold',
          cursor: isSpawning || !canUseDevTools ? 'not-allowed' : 'pointer',
          opacity: isSpawning || !canUseDevTools ? 0.6 : 1,
          marginBottom: '6px',
        }}
      >
        {isSpawning ? 'Spawning...' : (category === 'construction' ? '🔨 Spawn Kits' : '📝 Spawn Signs')}
      </button>

      {/* Results */}
      {error && (
        <div style={{
          padding: '6px',
          backgroundColor: '#4a1a1a',
          color: '#ff6b6b',
          fontSize: '10px',
          borderRadius: '4px',
          marginBottom: '6px',
        }}>
          {error}
        </div>
      )}

      {lastResult && lastResult.success && (
        <div style={{
          padding: '6px',
          backgroundColor: '#1a4a1a',
          color: '#6bff6b',
          fontSize: '10px',
          borderRadius: '4px',
          marginBottom: '6px',
        }}>
          <div>{lastResult.message}</div>
          {lastResult.constructionId && (
            <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>
              ID: {lastResult.constructionId.substring(0, 12)}...
            </div>
          )}
          {lastResult.worldX !== undefined && lastResult.worldY !== undefined && (
            <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>
              Pos: ({lastResult.worldX}, {lastResult.worldY})
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: '9px', color: '#718096', lineHeight: '1.4' }}>
        💡 {category === 'construction' 
          ? 'Spawns construction kits to inventory. Place tiles in the Reaches region.' 
          : 'Spawns signs to inventory. Place signs with custom text in the Reaches region.'}
      </div>
    </div>
  );
};
